import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { Provider as DrawerProvider } from "./context/Drawer.context";
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  HttpLink,
  from,
  ApolloLink,
  useMutation,
} from "@apollo/client";
import Theme from "./utils/Theme";
import { CssBaseline, Box, CircularProgress } from "@mui/material";

import { createAuthLink } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";

import Layout from "./component/layout/Layout.component";
import RequiredAuth from "./component/base/RequiredAuth.component";
import HomePage from "./page/Home.page";
import LoginPage from "./page/Login.page";
import ManageUsersPage from "./page/ManageUsers.page";
import ReportsPage from "./page/Reports.page";
import InsurancePage from "./page/Insurance.page";
import InValidPage from "./page/InValid.page";
import { AuthProvider } from "./context/Auth.context";
import CustomersPage from "./page/Customers.page";
import VerificationPage from "./page/Verification.page";
import GetUserDetails from "./page/GetUserDetails.page";
import ScrollToTop from "./utils/scrollToTop.utils";
import { VerificationProvider } from "./context/Verification.context";
import { AppProvider } from "./context/App.context";
import ForgotPasswordPage from "./page/ForgotPassword.page";
import { Settings } from "@mui/icons-material";
import DealersPage from "./page/Dealership.page";
import TransactionPage from "./page/Transaction.page";
import DealershipUserPage from "./page/DealershipUser.page";
import AddDealershipDialog from "./component/app/admin/dealership/dialog/dialogBody/AddDealership.dialog";
import CreateDealershipPage from "./page/CreateDealership.page";
import SelectMFAComponent from "./component/app/login/SelectMfa.component";
import UnSubscribeComponent from "./component/app/verification/UnSubscribe.component";
import ApiServicePage from "./page/ApiService.page";
import EditDealershipPage from "./page/EditDealership.page";
import SubmitReferrals from "./component/app/verification/document/artifactResponse/SubmitReferral";
import OmniServicePage from "./page/OmniService.page";
import OmniUserDetails from "./component/app/omniService/OmniUserDetails.component";
import ServiceListingComponent from "./component/app/omniService/ServiceListing.component";
import KnowledgeHub from "./page/KnowledgeHub.page";
import GroupDashboardComponent from "./component/GroupDashboard.component";
import SubmitVehicle from "./component/app/verification/document/artifactResponse/SubmitVehicle";
import SubmitCsi from "./component/app/verification/document/artifactResponse/SubmitCsi";
import { FOLLOWUP_REVIEW_CLICK } from "./graphql/mutation/mutation";

/*UAT Credentials*/

// const API_URL = process.env.REACT_APP_APIURL
//   ? process.env.REACT_APP_APIURL
//   : "https://kvk7mgf5qnhgdidvpncoh2v6qu.appsync-api.us-east-2.amazonaws.com/graphql";
// const API_KEY = process.env.REACT_APP_APIKEY
//   ? process.env.REACT_APP_APIKEY
//   : "da2-dwhlzfve6vc6jg2q7y7nw3tnua";

/*DEV Credentials*/
const API_URL = process.env.REACT_APP_APIURL
  ? process.env.REACT_APP_APIURL
  : "https://237sbcxarnevraevfcqkbmyrka.appsync-api.us-east-2.amazonaws.com/graphql"; //https://zgpfezr5drd7jptbexb3pkgbxq.appsync-api.us-east-2.amazonaws.com/graphql
const API_KEY = process.env.REACT_APP_APIKEY
  ? process.env.REACT_APP_APIKEY
  : "da2-bqq3bleqqvb3vlu4zwm2bxxx7y";

const region = "us-east-2";
const auth = {
  type: "API_KEY",
  apiKey: API_KEY,
};

const httpLink = from([
  new HttpLink({
    uri: API_URL,
  }),
]);

const link = ApolloLink.from([
  createAuthLink({ url: API_URL, region, auth }),
  createSubscriptionHandshakeLink(
    { url: API_URL, region, auth, disableOffline: true },
    httpLink
  ),
]);

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: link,
});

// Public page opened from the follow-up review email/SMS logos.
// Marks is_followup_review = true, then redirects to the real review site.
const ReviewLinkRedirect = () => {
  const { userId, option } = useParams();
  const [track] = useMutation(FOLLOWUP_REVIEW_CLICK);

  useEffect(() => {
    track({
      variables: {
        followupReviewInput: { user_id: parseInt(userId), option },
      },
    })
      .then(({ data }) => {
        const url = data?.followupReviewClick?.url;
        if (url)
          window.location.replace(
            /^https?:\/\//i.test(url) ? url : `https://${url}`
          );
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <CircularProgress />
    </Box>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={Theme}>
      <CssBaseline />
      <ApolloProvider client={client}>
        <AuthProvider>
          <AppProvider>
            <VerificationProvider>
              <DrawerProvider>
                <BrowserRouter>
                  <ScrollToTop>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <RequiredAuth>
                            <Layout />
                          </RequiredAuth>
                        }
                      >
                        <Route index element={<HomePage />} />
                        <Route
                          path="groupDashboard"
                          element={<GroupDashboardComponent />}
                        />
                        <Route
                          path="transactions"
                          element={<TransactionPage />}
                        />
                        <Route
                          path="createDealership/dealership"
                          element={<CreateDealershipPage />}
                        />
                        <Route
                          path="createDealership/:dealershipId/avatars"
                          element={<CreateDealershipPage />}
                        />
                        <Route
                          path="createDealership/:dealershipId/artifacts"
                          element={<CreateDealershipPage />}
                        />
                        <Route
                          path="createDealership/:dealershipId"
                          element={<EditDealershipPage />}
                        />

                        <Route
                          path="dealerships/:dealershipId"
                          element={<DealersPage />}
                        />
                        <Route
                          path="dealershipUsers/:dealershipId"
                          element={<DealershipUserPage />}
                        />

                        <Route
                          path="manageUsers"
                          element={<ManageUsersPage />}
                        />
                        <Route path="reports" element={<ReportsPage />} />
                        <Route path="customers" element={<CustomersPage />} />
                        <Route
                          path="customers/:employeeId"
                          element={<CustomersPage />}
                        />
                        <Route path="insurance" element={<InsurancePage />} />
                      </Route>

                      <Route path="/login" element={<LoginPage />} />
                      {/* <Route path="/omniLogin" element={<ServiceListingComponent />} /> */}

                      <Route path="/express">
                        <Route
                          path="service"
                          element={<ServiceListingComponent />}
                        />
                        <Route index element={<OmniServicePage />} />
                        <Route
                          path="getUserDetails/:spid/:dealershipId/:workflowId"
                          element={<OmniUserDetails />}
                        />
                        {/* <Route path="knowledgeHub/:spId/:dealershipId/:customerId/:page" element={<KnowledgeHub />} /> */}
                      </Route>

                      <Route path="/verification">
                        <Route
                          path="consent/:spid/:dealershipId/:customer/:isWalkthrough/:pageCount/:isRepull"
                          element={<VerificationPage route={"consent"} />}
                        />
                        <Route
                          path="consent/:spid/:dealershipId/:customer/:pageCount/:isRepull"
                          element={<VerificationPage route={"consent"} />}
                        />
                        {/* <Route
                          path="consent/services/:spid/:dealershipId/:customer/:isWalkthrough/:isOmniUser"
                          element={<VerificationPage route={"consent"} />}
                        />  */}
                        <Route
                          path="knowledgeHub"
                          element={<VerificationPage route={"knowledgeHub"} />}
                        />
                        <Route
                          path="selectProvider"
                          element={
                            <VerificationPage route={"selectProvider"} />
                          }
                        />
                        <Route
                          path="providerLogin"
                          element={<VerificationPage route={"providerLogin"} />}
                        />
                        <Route
                          path="verifyInsurance"
                          element={
                            <VerificationPage route={"verifyInsurance"} />
                          }
                        />
                        <Route
                          path="tradeVehicle"
                          element={<VerificationPage route={"tradeVehicle"} />}
                        />
                        <Route
                          path="requestQuote"
                          element={<VerificationPage route={"requestQuote"} />}
                        />
                        <Route
                          path="questionnaires"
                          element={
                            <VerificationPage route={"questionnaires"} />
                          }
                        />
                        <Route
                          path="thankYou"
                          element={<VerificationPage route={"thankYou"} />}
                        />
                      </Route>

                      <Route
                        // path="/getUserDetails/:encryptData"
                        path="/getUserDetails/:spid/:dealershipId/:workflowId"
                        element={<GetUserDetails />}
                      />
                      <Route
                        path="/userDetails/:transactionId"
                        element={<ApiServicePage />}
                      />
                      <Route
                        path="/resetPassword/:data"
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path="/unsubscribe/:userId"
                        element={<UnSubscribeComponent />}
                      />
                      <Route
                        path="/referrals/:dealershipId/:employeeId/:userId"
                        element={<SubmitReferrals />}
                      />
                      <Route
                        path="/review/:dealershipId/:employeeId/:userId"
                        element={<SubmitCsi />}
                      />
                      <Route
                        path="/review-link/:userId/:option"
                        element={<ReviewLinkRedirect />}
                      />
                      <Route
                        path="/service-schedule/:dealershipId/:employeeId/:userId/:followupNumber/:vehicleId/:isNewVehicle?"
                        element={<SubmitVehicle />}
                      />

                      <Route path="*" element={<InValidPage />} />
                    </Routes>
                  </ScrollToTop>
                </BrowserRouter>
              </DrawerProvider>
            </VerificationProvider>
          </AppProvider>
        </AuthProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
};

export default App;